import React, { useState, useEffect, useRef } from 'react';

/**
 * AutoHideHeader - Wrapper component that auto-hides children after inactivity
 *
 * Features:
 * - Auto-hides after specified timeout (default 5 seconds)
 * - Shows again on tap/click anywhere on the hidden bar area
 * - Shows on swipe down from top edge
 * - Smooth slide animation
 *
 * Usage:
 * <AutoHideHeader timeout={5000}>
 *   <YourHeaderContent />
 * </AutoHideHeader>
 */
export const AutoHideHeader = ({
  children,
  timeout = 5000,  // Time in ms before auto-hiding
  className = '',
  alwaysShow = false  // Set true to disable auto-hide (for desktop/testing)
}) => {
  const [isVisible, setIsVisible] = useState(true);
  const [isPeeking, setIsPeeking] = useState(false); // For the hint bar
  const timeoutRef = useRef(null);
  const containerRef = useRef(null);

  // Reset the hide timer
  const resetTimer = () => {
    if (alwaysShow) return;

    setIsVisible(true);

    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    timeoutRef.current = setTimeout(() => {
      setIsVisible(false);
    }, timeout);
  };

  // Initialize timer on mount
  useEffect(() => {
    resetTimer();

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [timeout, alwaysShow]);

  // Handle user interaction to show header
  const handleInteraction = () => {
    if (!isVisible) {
      resetTimer();
    }
  };

  // Touch handling for swipe-down gesture
  const touchStartY = useRef(0);
  const handleTouchStart = (e) => {
    touchStartY.current = e.touches[0].clientY;
  };

  const handleTouchMove = (e) => {
    const touchY = e.touches[0].clientY;
    const deltaY = touchY - touchStartY.current;

    // If swiping down from near top edge (within 50px)
    if (touchStartY.current < 50 && deltaY > 30) {
      handleInteraction();
    }
  };

  return (
    <div className="relative z-50">
      {/* Hidden state: Show a thin bar at top as hint */}
      {!isVisible && (
        <div
          className="absolute top-0 left-0 right-0 h-2 bg-gradient-to-b from-blue-500/30 to-transparent cursor-pointer z-40"
          onClick={handleInteraction}
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onMouseEnter={() => setIsPeeking(true)}
          onMouseLeave={() => setIsPeeking(false)}
        >
          {/* Chevron hint that appears on hover/touch */}
          {isPeeking && (
            <div className="absolute top-1 left-1/2 transform -translate-x-1/2 text-white text-xs animate-bounce">
              ▼
            </div>
          )}
        </div>
      )}

      {/* The actual header content */}
      <div
        ref={containerRef}
        className={`
          transition-transform duration-300 ease-in-out
          ${isVisible ? 'translate-y-0' : '-translate-y-full'}
          ${className}
        `}
        onClick={isVisible ? resetTimer : undefined}
        onTouchStart={isVisible ? resetTimer : undefined}
      >
        {children}
      </div>
    </div>
  );
};
